import React, { useState, useEffect, Suspense, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import DefaultLoading from '../common/DefaultLoading';
import WebFont from 'webfontloader';
import { decode } from 'js-base64';

// Cache for game components to avoid re-importing
const componentCache = new Map();

// Cache for game data to reduce Firestore calls
const gameDataCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_CACHE_SIZE = 50; // Maximum number of cached items

// Cache cleanup function
const cleanupCaches = () => {
  const now = Date.now();
  
  // Clean up expired game data
  for (const [key, value] of gameDataCache.entries()) {
    if (now - value.timestamp > CACHE_TTL) {
      gameDataCache.delete(key);
    }
  }
  
  // If cache is still too large, remove oldest entries
  if (gameDataCache.size > MAX_CACHE_SIZE) {
    const entries = Array.from(gameDataCache.entries());
    entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
    const toRemove = entries.slice(0, gameDataCache.size - MAX_CACHE_SIZE);
    toRemove.forEach(([key]) => gameDataCache.delete(key));
  }
  
  // Clean up component cache if it gets too large
  if (componentCache.size > 20) {
    const entries = Array.from(componentCache.entries());
    const toRemove = entries.slice(0, componentCache.size - 20);
    toRemove.forEach(([key]) => componentCache.delete(key));
  }
};

// Run cache cleanup every 5 minutes
setInterval(cleanupCaches, 5 * 60 * 1000);

const Play = ({ previewData, isPreview }) => {
  const { id } = useParams();
  const [gameData, setGameData] = useState(previewData);
  const [GameComponent, setGameComponent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const loadingTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Enhanced error handling with user-friendly messages
  const handleError = useCallback((error, context) => {
    console.error(`Error in ${context}:`, error);
    
    let userMessage = 'Something went wrong loading the game.';
    
    if (error.name === 'AbortError') {
      userMessage = 'Loading was cancelled.';
    } else if (error.message?.includes('timeout') || error.message?.includes('network')) {
      userMessage = 'Connection timeout. Please check your internet and try again.';
    } else if (error.message?.includes('not found') || error.message?.includes('404')) {
      userMessage = 'Game not found. The link might be invalid or the game may have been removed.';
    } else if (error.message?.includes('permission') || error.message?.includes('403')) {
      userMessage = 'You don\'t have permission to access this game.';
    } else if (error.code === 'unavailable') {
      userMessage = 'Game service is temporarily unavailable. Please try again in a moment.';
    }
    
    setError({ message: userMessage, canRetry: !error.message?.includes('not found') });
  }, []);

  // Retry mechanism
  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryCount(prev => prev + 1);
  }, []);

  // Load game component with caching and timeout
  const loadGameComponent = useCallback(async (gameType) => {
    if (!gameType) return null;

    // Check cache first
    const cacheKey = gameType;
    if (componentCache.has(cacheKey)) {
      return componentCache.get(cacheKey);
    }

    try {
      // Set timeout for component loading
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Component loading timeout')), 10000);
      });

      const importPromise = import(`../games/${gameType}/Main`);
      
      const module = await Promise.race([importPromise, timeoutPromise]);
      const component = React.lazy(() => Promise.resolve(module));
      
      // Cache the component
      componentCache.set(cacheKey, component);
      return component;
    } catch (error) {
      console.error(`Failed to load game component for ${gameType}:`, error);
      throw new Error(`Failed to load game component: ${error.message}`);
    }
  }, []);

  // Load game data with caching and timeout
  const loadGameData = useCallback(async () => {
    if (!id) return null;

    // Check cache first
    const cacheKey = `game_${id}`;
    const cached = gameDataCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      console.log(`Cache hit for game ID: ${id}`);
      return cached.data;
    }

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();
    
    try {
      // Set timeout for Firestore request
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Database timeout')), 8000);
      });

      const docRef = doc(db, 'campaign', id);
      const firestorePromise = getDoc(docRef);
      
      const docSnap = await Promise.race([firestorePromise, timeoutPromise]);
      
      if (abortControllerRef.current?.signal.aborted) {
        throw new Error('Request was cancelled');
      }

      if (!docSnap.exists()) {
        throw new Error('Game not found');
      }

      const data = docSnap.data();
      
      // Cache the data
      gameDataCache.set(cacheKey, { data, timestamp: Date.now() });
      console.log(`Cached game data for ID: ${id}`);
      
      return data;
    } catch (error) {
      if (error.name === 'AbortError' || error.message === 'Request was cancelled') {
        throw new Error('Request was cancelled');
      }
      throw error;
    }
  }, [id]);

  // Main loading effect
  useEffect(() => {
    const loadGame = async () => {
      // Set overall timeout for the entire loading process
      loadingTimeoutRef.current = setTimeout(() => {
        handleError(new Error('Loading timeout'), 'overall timeout');
        setLoading(false);
      }, 15000); // 15 second overall timeout

      try {
        let data = previewData;
        
        if (!isPreview) {
          // Check URL for preview data first
          const urlParams = new URLSearchParams(window.location.search);
          const previewParam = urlParams.get('preview');
          
          if (previewParam) {
            try {
              data = JSON.parse(decode(previewParam));
            } catch (decodeError) {
              console.error('Error decoding preview data:', decodeError);
              // Fall back to loading from database
              data = await loadGameData();
            }
          } else {
            // Load from database
            data = await loadGameData();
          }
        }

        if (!data) {
          throw new Error('No game data found');
        }

        // Add the ID to the game data
        const gameDataWithId = {
          ...data,
          id: id || previewData?.id || 'preview'
        };

        // Set game data
        setGameData(gameDataWithId);

        // Load game component
        if (data.gameType) {
          const component = await loadGameComponent(data.gameType);
          setGameComponent(() => component);
        } else {
          throw new Error('Game type not specified');
        }

        // Clear any previous errors
        setError(null);
        
      } catch (error) {
        handleError(error, 'loadGame');
      } finally {
        cleanup();
        setLoading(false);
      }
    };

    // Reset state when dependencies change
    setLoading(true);
    setError(null);
    
    loadGame();

    // Cleanup on unmount or dependency change
    return cleanup;
  }, [previewData, isPreview, id, retryCount, loadGameData, loadGameComponent, handleError, cleanup]);

  // Message listener for preview updates
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === 'PREVIEW_UPDATE') {
        setGameData({
          ...event.data.gameData,
          id: id || 'preview'
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [id]);

  // Loading state
  if (loading) {
    return <DefaultLoading message="Loading game..." />;
  }

  // Error state
  if (error) {
    return (
      <div 
        className="absolute inset-0 flex items-center justify-center"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
      >
        <div className="text-center max-w-md mx-auto p-8 rounded-xl border-2" style={{
          backgroundColor: '#34495E',
          borderColor: '#E74C3C',
          fontFamily: "'Share Tech Mono', monospace"
        }}>
          <div className="text-6xl mb-4">💥</div>
          <h2 className="text-2xl font-bold mb-3" style={{ color: '#F1C40F' }}>GAME ERROR!</h2>
          <p className="mb-6 font-bold" style={{ color: '#ECF0F1' }}>{error.message}</p>
          {error.canRetry && (
            <button
              onClick={retry}
              className="px-6 py-3 rounded-lg font-bold border-2 transition-all duration-200 hover:scale-105 mb-4 block w-full"
              style={{
                backgroundColor: '#E74C3C',
                color: '#F1C40F',
                borderColor: '#C0392B'
              }}
            >
              🔄 TRY AGAIN
            </button>
          )}
          <button
            onClick={() => window.location.href = '/'}
            className="text-sm font-bold hover:scale-105 transition-all duration-200"
            style={{ color: '#3498DB' }}
          >
            ← BACK TO HOME
          </button>
        </div>
      </div>
    );
  }

  // Game not found state
  if (!gameData || !GameComponent) {
    return (
      <div 
        className="absolute inset-0 flex items-center justify-center"
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.8)'
        }}
      >
        <div className="text-center max-w-md mx-auto p-8 rounded-xl border-2" style={{
          backgroundColor: '#34495E',
          borderColor: '#E67E22',
          fontFamily: "'Share Tech Mono', monospace"
        }}>
          <div className="text-6xl mb-4">🎮</div>
          <h2 className="text-2xl font-bold mb-3" style={{ color: '#F1C40F' }}>GAME NOT FOUND</h2>
          <p className="mb-6 font-bold" style={{ color: '#ECF0F1' }}>
            The game you're looking for doesn't exist or may have been removed.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="px-6 py-3 rounded-lg font-bold border-2 transition-all duration-200 hover:scale-105"
            style={{
              backgroundColor: '#3498DB',
              color: '#F1C40F',
              borderColor: '#2980B9'
            }}
          >
            🏠 BACK TO HOME
          </button>
        </div>
      </div>
    );
  }

  // Successful game render
  return (
    <Suspense 
      fallback={<DefaultLoading message="Starting game..." />}
    >
      <div 
        className="absolute inset-0 flex items-center justify-center"
        style={{
          fontFamily: gameData?.options?.font || 'inherit',
          overflow: 'hidden',
        }}
      >
        <div className="relative w-full h-full">
          <GameComponent gameData={gameData} />
        </div>
      </div>
    </Suspense>
  );
};

export default Play;